import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  SxProps,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";

import type {
  AdditionalFormFieldProps,
  BaseFormFieldDefinition,
} from "../form-field";

export const RADIO_FORM_FIELD_TYPE = "radio" as const;

export type RadioFormFieldItem = {
  value: string;
  label: string;
};

export type RadioFormFieldDefinition = BaseFormFieldDefinition<
  typeof RADIO_FORM_FIELD_TYPE
> & {
  type: typeof RADIO_FORM_FIELD_TYPE;
  items: RadioFormFieldItem[];
  row?: boolean;
  sx?: SxProps;
};

/**
 * Controlled radio group — integrates with react-hook-form via Controller.
 * This is the default used by PimoForm.
 */
export function RadioFormField({
  disabled,
  errorMessage,
  items,
  name,
  required,
  row,
  sx,
}: RadioFormFieldDefinition & AdditionalFormFieldProps) {
  const { control } = useFormContext();

  // Detect whether all item values are boolean-like ("true"/"false"),
  // so we can round-trip boolean field values without string coercion in callers.
  const isBooleanRadio = items.every(
    (item) => item.value === "true" || item.value === "false"
  );

  function toDisplayValue(raw: unknown): string {
    if (raw === true) return "true";
    if (raw === false) return "false";
    return raw == null ? "" : String(raw);
  }

  function fromDisplayValue(str: string): unknown {
    if (isBooleanRadio) {
      if (str === "true") return true;
      if (str === "false") return false;
      return null;
    }
    return str;
  }

  return (
    <FormControl error={!!errorMessage} fullWidth sx={sx}>
      <Controller
        control={control}
        name={name}
        rules={{ required: required?.value ? required.message : false }}
        render={({ field }) => (
          <RadioGroup
            {...field}
            value={toDisplayValue(field.value)}
            onChange={(e) => field.onChange(fromDisplayValue(e.target.value))}
            row={row}
          >
            {items.map((item) => (
              <FormControlLabel
                key={item.value}
                value={item.value}
                disabled={disabled}
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: "14px" }}>
                    {item.label}
                  </Typography>
                }
              />
            ))}
          </RadioGroup>
        )}
      />
      {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
    </FormControl>
  );
}

/**
 * Uncontrolled radio group — integrates with react-hook-form via register.
 * Use this when you need uncontrolled behaviour.
 */
export function RadioFormFieldUncontrolled({
  disabled,
  errorMessage,
  items,
  label,
  name,
  register,
  required,
  row,
  sx,
  value,
}: RadioFormFieldDefinition & AdditionalFormFieldProps) {
  return (
    <FormControl error={!!errorMessage} fullWidth sx={sx}>
      {label && (
        <Typography sx={{ fontSize: "14px", mb: 0.5 }}>{label}</Typography>
      )}
      <RadioGroup defaultValue={value as string} row={row}>
        {items.map((item) => (
          <FormControlLabel
            key={item.value}
            value={item.value}
            control={
              <Radio
                {...register(name, {
                  disabled,
                  required: required?.value ? required.message : false,
                })}
                size="small"
                value={item.value}
              />
            }
            label={
              <Typography sx={{ fontSize: "14px" }}>{item.label}</Typography>
            }
          />
        ))}
      </RadioGroup>
      {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
    </FormControl>
  );
}
